import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getDay, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import * as S from './Calendar.styles';

export interface SessionEvent {
  id: string | number;
  date: Date;
  className: string;
  subject: string;
  time: string;
  tutorName?: string;
  studentName?: string;
  classId?: number;
  courseId?: number;
  status?: 'upcoming' | 'completed' | 'cancelled';
  location?: 'online' | 'in person';
  address?: string;
}

interface CalendarProps {
  sessions: SessionEvent[];
  onSessionClick?: (session: SessionEvent) => void;
  userType?: 'student' | 'tutor';
}

const Calendar: React.FC<CalendarProps> = React.memo(({ sessions, onSessionClick, userType = 'student' }) => {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState<Date | null>(today);
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');

  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  
  const startDate = viewMode === 'week' ? weekStart : startOfWeek(monthStart);
  const endDate = viewMode === 'week' ? weekEnd : endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekDaysShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const navigatePrevious = () => {
    if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const getDateRangeText = () => {
    if (viewMode === 'week') {
      const start = format(weekStart, 'MMM d');
      const end = format(weekEnd, 'MMM d, yyyy');
      return `${start} - ${end}`;
    }
    return format(currentDate, 'MMMM yyyy');
  };

  const getSessionsForDay = (day: Date) => {
    return sessions.filter(session => isSameDay(session.date, day));
  };

  const handleDayClick = (day: Date) => {
    setSelectedDate(day);
    const daySessions = getSessionsForDay(day);
    // In month view, if only one session, navigate directly to it
    if (viewMode === 'month' && daySessions.length === 1 && onSessionClick) {
      onSessionClick(daySessions[0]);
    }
  };

  return (
    <S.CalendarContainer>
      <S.CalendarHeader>
        <S.NavigationButton onClick={navigatePrevious}>
          <S.NavIcon>‹</S.NavIcon>
        </S.NavigationButton>
        <S.MonthYear>{getDateRangeText()}</S.MonthYear>
        <S.NavigationButton onClick={navigateNext}>
          <S.NavIcon>›</S.NavIcon>
        </S.NavigationButton>
      </S.CalendarHeader>

      <S.ViewToggle>
        <S.ViewButton 
          $active={viewMode === 'week'} 
          onClick={() => setViewMode('week')}
        >
          Week
        </S.ViewButton>
        <S.ViewButton 
          $active={viewMode === 'month'} 
          onClick={() => setViewMode('month')}
        >
          Month
        </S.ViewButton>
      </S.ViewToggle>

      <S.WeekDaysRow>
        {(viewMode === 'week' ? weekDays : weekDaysShort).map((day, index) => (
          <S.WeekDay key={index}>{day}</S.WeekDay>
        ))}
      </S.WeekDaysRow>

      <S.DaysGrid $viewMode={viewMode}>
        {days.map((day, index) => {
          const daySessions = getSessionsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isToday = isSameDay(day, new Date());
          const isSelected = selectedDate && isSameDay(day, selectedDate);

          return (
            <S.DayCell
              key={index}
              $isCurrentMonth={isCurrentMonth}
              $isToday={isToday}
              $isSelected={!!isSelected}
              $hasEvents={daySessions.length > 0}
              $viewMode={viewMode}
              onClick={() => handleDayClick(day)}
            >
              <S.DayHeader>
                <S.DayNumber $isCurrentMonth={isCurrentMonth} $isToday={isToday} $isSelected={!!isSelected}>{format(day, 'd')}</S.DayNumber>

              </S.DayHeader>
              
              {viewMode === 'month' && daySessions.length > 0 && (
                <S.EventIndicators>
                  {daySessions.slice(0, 3).map((session, idx) => (
                    <S.EventDot
                      key={idx}
                      $status={session.status || 'upcoming'}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSessionClick && onSessionClick(session);
                      }}
                    />
                  ))}
                  {daySessions.length > 3 && (
                    <S.MoreEvents>+{daySessions.length - 3}</S.MoreEvents>
                  )}
                </S.EventIndicators>
              )}
              
              {viewMode === 'week' && daySessions.length > 0 && (
                <S.SessionCount>{daySessions.length}</S.SessionCount>
              )}
            </S.DayCell>
          );
        })}
      </S.DaysGrid>

      {selectedDate && getSessionsForDay(selectedDate).length > 0 && (
        <S.SessionsPanel>
          <S.SessionsPanelHeader>
            <S.SessionsPanelTitle>
              Sessions on {format(selectedDate, 'MMMM d, yyyy')}
            </S.SessionsPanelTitle>
          </S.SessionsPanelHeader>
          <S.SessionsList>
            {getSessionsForDay(selectedDate).map((session) => (
              <S.SessionCard
                key={session.id}
                onClick={() => onSessionClick && onSessionClick(session)}
              >
                <S.SessionTime>{session.time}</S.SessionTime>
                <S.SessionDetails>
                  <S.SessionClassName>{session.className}</S.SessionClassName>
                  <S.SessionSubject>{session.subject}</S.SessionSubject>
                  {userType === 'student' && session.tutorName && (
                    <S.SessionMeta>with {session.tutorName}</S.SessionMeta>
                  )}
                  {userType === 'tutor' && session.studentName && (
                    <S.SessionMeta>with {session.studentName}</S.SessionMeta>
                  )}
                </S.SessionDetails>
                <S.SessionStatus $status={session.status || 'upcoming'}>
                  {session.status || 'upcoming'}
                </S.SessionStatus>
              </S.SessionCard>
            ))}
          </S.SessionsList>
        </S.SessionsPanel>
      )}
      
      {selectedDate && getSessionsForDay(selectedDate).length === 0 && (
        <S.EmptySessionsPanel>
          <S.EmptySessionsText>No sessions scheduled for {format(selectedDate, 'MMMM d, yyyy')}</S.EmptySessionsText>
        </S.EmptySessionsPanel>
      )}
    </S.CalendarContainer>
  );
});

Calendar.displayName = 'Calendar';

export default Calendar;